import _ from "lodash";
import { useEffect, useState } from "react";

import {
  ButtonGroup,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  IconBarbell,
  IconCalendar,
  IconClock,
  IconSum,
} from "@tabler/icons-react";

import MainCard from "components/MainCard";
import LineChart from "features/charts/ChartLine";
import AlbumSelect from "features/charts/AlbumSelect";
import Image from "data/firestore/image";
import User from "data/firestore/user";
import CurrentSession from "data/userSession";
import AnalyticsService from "data/firestore/analytics";
import { cyan } from "@mui/material/colors";

const Timeline = ({
  uid,
  onTimelineDataChange,
  mainMetric: initialMainMetric,
  onMainMetricChange,
}) => {
  const [brushRange, setBrushRange] = useState(null);
  const [aggregated, setAggregated] = useState(true);
  const [albums, setAlbums] = useState([]);
  const [images, setImages] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [viewedUserAbove18, setViewedUserAbove18] = useState(false);
  const [currentUserAbove18, setCurrentUserAbove18] = useState(false);
  const [localMainMetric, setLocalMainMetric] = useState(
    initialMainMetric || "Hours"
  );

  useEffect(() => {
    CurrentSession.load().then((user) => {
      setCurrentUserAbove18(user.above18 || false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      return;
    }

    updateTimeline();
    Image.list(uid)
      .then((images) => {
        images = _.filter(images, (i) => i.favorited);
        const sortedAlbums = _.uniq(_.map(images, (img) => img.album)).sort();
        setAlbums(sortedAlbums);
        setSelectedAlbum("");
        setImages(images);
      })
      .catch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  useEffect(() => {
    if (!uid) {
      return;
    }
    updateTimeline(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregated, images]);

  function setMetric(metric) {
    setLocalMainMetric(metric);
    if (onMainMetricChange) {
      onMainMetricChange(metric);
    }
    AnalyticsService.event("set_timeline_metric", { metric: metric });
  }

  function updateTimeline() {
    const user = new User(uid);
    user.load(true).then(() => {
      user.snapshot.images = images;
      const computedTimeline = user.snapshot.timeline(aggregated);
      setTimeline(computedTimeline);
      setIsEmpty((_.sumBy(computedTimeline, "sessions") || 0) === 0);
      // Check viewed user's above18 status for image tiles
      setViewedUserAbove18(user.above18 || false);
      // Pass timeline data to parent
      if (onTimelineDataChange) {
        onTimelineDataChange(computedTimeline);
      }
    });
  }

  return (
    <MainCard
      title="My Journey"
      secondary={
        <ButtonGroup aria-label="icon button group">
          <Tooltip title="Days">
            <IconButton
              onClick={() => setMetric("Days")}
              color={localMainMetric === "Days" ? "primary" : "grey"}
            >
              <IconCalendar />
            </IconButton>
          </Tooltip>
          <Tooltip title="Hours">
            <IconButton
              onClick={() => setMetric("Hours")}
              color={localMainMetric === "Hours" ? "error" : "grey"}
            >
              <IconClock />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tug Sessions">
            <IconButton
              onClick={() => setMetric("Sessions")}
              color={localMainMetric === "Sessions" ? "warning" : "grey"}
            >
              <IconBarbell />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
      }
      contentSX={{ padding: 1 }}
    >
      {isEmpty ? (
        <Typography variant="overline">
          No completed tug sessions yet
        </Typography>
      ) : (
        <Grid container flexDirection="column" alignContent="center">
          <Grid item style={{ flex: 1, width: "100%" }}>
            <LineChart
              uid={uid}
              data={timeline}
              metric={_.lowerCase(localMainMetric)}
              selectedAlbum={selectedAlbum}
              showAllImages={true}
              aggregated={aggregated}
              brushRange={brushRange}
              setBrushRange={setBrushRange}
              viewedUserAbove18={viewedUserAbove18}
              currentUserAbove18={currentUserAbove18}
            />
          </Grid>

          <Grid
            container
            flex={0}
            style={{ margin: 5, justifyContent: "space-around" }}
            alignItems="center"
          >
            <Grid item>
              <Tooltip title="Aggregate data">
                <IconButton
                  onClick={() => {
                    setAggregated(!aggregated);
                    AnalyticsService.event("set_timeline_aggregated", {
                      aggregated: !aggregated,
                    });
                  }}
                  style={{
                    color: aggregated ? cyan[500] : "grey",
                    // backgroundColor: aggregated ? cyan[500] : "transparent",
                  }}
                >
                  <IconSum />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <AlbumSelect
                albums={albums}
                album={selectedAlbum}
                allowChanges={true}
                numberOfImages={images.length}
                onChange={(album) => {
                  setSelectedAlbum(album);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </MainCard>
  );
};

export default Timeline;
